import axios from 'axios'
import type { InternalAxiosRequestConfig } from 'axios'
import { useTokenExpiration } from '../../composables/useTokenExpiration'
import { useAuthStore } from '../../stores/auth'
import logger from '../../utils/logger'
import { isTeacherSession } from '../../utils/isTeacherSession'
import { fetchSessionExpiresIn, resolveSessionCountdownSeconds } from '../../utils/sessionExpiresIn'

const CSRF_HEADER = 'X-CSRF-Token'
const CSRF_STORAGE_KEY = 'csrf'
const CSRF_COOKIE_NAME = 'csrf'

const readCsrfCookie = (): string => {
  if (typeof document === 'undefined') return ''
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${CSRF_COOKIE_NAME}=`))
  return match ? decodeURIComponent(match.split('=')[1]) : ''
}

const getStoredCsrf = (): string => {
  if (typeof window === 'undefined') return ''
  const stored = localStorage.getItem(CSRF_STORAGE_KEY) || ''
  const fromCookie = readCsrfCookie()

  // Prefer cookie value when available because the server may rotate CSRF cookies
  // (e.g., token refresh, multi-instance deployments, session changes).
  // Keeping localStorage in sync prevents header/cookie mismatches in production.
  if (fromCookie) {
    if (fromCookie !== stored) {
      localStorage.setItem(CSRF_STORAGE_KEY, fromCookie)
    }
    return fromCookie
  }

  // Fallback to stored token in localStorage
  return stored
}

const storeCsrf = (token: string | undefined | null) => {
  if (typeof window === 'undefined') return
  if (token) {
    localStorage.setItem(CSRF_STORAGE_KEY, token)
  }
}

const otpLoginHref = () => {
  // Hash router in production: `/#/login?focus=teacher` keeps the SPA on index.html.
  return import.meta.env.PROD ? '/#/login?focus=teacher' : '/login?focus=teacher'
}

let csrfFetchPromise: Promise<string> | null = null

// Shared state for token refresh (used by both proactive and reactive refresh)
let isRefreshing = false
let pendingRequests: Array<() => void> = []
let isShowingModal = false // Track if expiration modal is already being shown
let modalResolved = false // Track if user has responded to modal

const ensureCsrfToken = async (): Promise<string> => {
  // First check localStorage
  const stored = getStoredCsrf()
  if (stored) {
    return stored
  }

  // Then check cookie (CSRF cookie is NOT httpOnly, so it's accessible)
  const cookieCsrf = readCsrfCookie()
  if (cookieCsrf) {
    storeCsrf(cookieCsrf)
    return cookieCsrf
  }

  // Finally, fetch from API if not available
  if (!csrfFetchPromise) {
    csrfFetchPromise = axios
      .get('/api/sessions/csrf-token', { withCredentials: true })
      .then((resp) => {
        const token = resp.data?.data?.csrfToken || ''
        if (token) {
          storeCsrf(token)
          return token
        }
        return ''
      })
      .catch(() => '')
      .finally(() => {
        csrfFetchPromise = null
      })
  }

  const token = await csrfFetchPromise
  return token || ''
}

const applyHeader = (headers: any, key: string, value: string) => {
  if (!headers || !value) return
  if (typeof headers.set === 'function') {
    headers.set(key, value)
  } else {
    headers[key] = value
  }
}

/**
 * Refresh the access token (now stored in HTTP-only cookie)
 * SECURITY: Tokens are no longer stored in localStorage - they're in HTTP-only cookies
 * This prevents XSS attacks from stealing tokens
 */
const refreshTokenProactively = async (): Promise<boolean> => {
  if (isRefreshing) {
    // If already refreshing, wait for it to complete
    return new Promise((resolve) => {
      pendingRequests.push(() => {
        resolve(true)
      })
    })
  }

  isRefreshing = true
  try {
    const csrf = await ensureCsrfToken()
    const resp = await axios.post(
      '/api/auth/refresh',
      {},
      { withCredentials: true, headers: { [CSRF_HEADER]: csrf } }
    )
    const newCsrf = resp.data?.data?.csrf || ''
    
    // SECURITY: Access token is now in HTTP-only cookie (not accessible to JavaScript)
    // Only update CSRF token in localStorage (needed for headers)
    if (newCsrf) {
      storeCsrf(newCsrf)
    }
    
    // Emit custom event to notify components that token was refreshed
    // This allows components to reload their data
    window.dispatchEvent(new CustomEvent('token-refreshed', { detail: { success: true } }))
    
    // Resolve all pending requests
    pendingRequests.forEach((cb) => cb())
    pendingRequests = []
    isRefreshing = false
    
    return true
  } catch (error) {
    isRefreshing = false
    pendingRequests = []
    // Emit event even on failure so components know to handle it
    window.dispatchEvent(new CustomEvent('token-refreshed', { detail: { success: false } }))
    // Don't throw - let the request proceed and handle 401 reactively
    return false
  }
}

export const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})

// Request interceptor: Attach CSRF token, access token is automatically sent via HTTP-only cookie
// SECURITY: Access tokens are now in HTTP-only cookies (not accessible to JavaScript)
// Cookies are automatically sent with requests when withCredentials: true
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    config.headers = (config.headers as any) || {}

    // If payload is FormData, remove default Content-Type so browser sets boundary automatically
    if (config.data instanceof FormData) {
      if (typeof config.headers.delete === 'function') {
        config.headers.delete('Content-Type')
        config.headers.delete('content-type')
      } else {
        delete config.headers['Content-Type']
        delete config.headers['content-type']
      }
    }

    // SECURITY: Access token is in HTTP-only cookie, automatically sent with request
    // No need to attach Authorization header - backend reads from cookie
    // Only attach CSRF token (needed for CSRF protection)
    const csrf = await ensureCsrfToken()
    if (csrf) {
      applyHeader(config.headers, CSRF_HEADER, csrf)
    }

    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor to refresh token on 401 and recover CSRF on 403
api.interceptors.response.use(
  (response) => {
    const newCsrf = response.headers?.[CSRF_HEADER.toLowerCase()] || response.headers?.[CSRF_HEADER]
    if (newCsrf) {
      storeCsrf(newCsrf as string)
    }
    return response
  },
  async (error) => {
    const original = error.config || {}

    // Handle rate limiting (429) - don't retry immediately, log and reject
    if (error.response?.status === 429) {
      const retryAfter = error.response?.headers?.['retry-after'] || error.response?.headers?.['Retry-After']
      const retryAfterMs = retryAfter ? parseInt(retryAfter) * 1000 : 60000 // Default to 60 seconds
      
      logger.warn('Rate limit exceeded (429)', {
        url: original.url,
        retryAfter: retryAfterMs,
        message: error.response?.data?.message || 'Too many requests'
      })
      
      // Don't retry rate limit errors - let the user know they need to wait
      return Promise.reject(error)
    }

    if (error.response?.status === 403 && error.response?.data?.code === 'CSRF_TOKEN_INVALID' && !original._csrfRetry) {
      original._csrfRetry = true
      try {
        const freshCsrf = await ensureCsrfToken()
        if (freshCsrf) {
          original.headers = original.headers || {}
          applyHeader(original.headers, CSRF_HEADER, freshCsrf)
          return api(original)
        }
      } catch (csrfError) {
        // fall through to default rejection
      }
    }

    if (error.response?.status === 401 && !original._retry) {
      // If modal is already showing, queue this request and wait for user response
      if (isShowingModal) {
        return new Promise((resolve, reject) => {
          pendingRequests.push(() => {
            if (modalResolved) {
              // User chose to continue - retry request with fresh token
              original.headers = original.headers || {}
              const csrf = getStoredCsrf()
              if (csrf) {
                applyHeader(original.headers, CSRF_HEADER, csrf)
              }
              resolve(api(original))
            } else {
              // User chose logout - reject the request
              reject(new Error('Session expired'))
            }
          })
        })
      }
      
      if (isRefreshing) {
        // Already refreshing, wait for it to complete
        return new Promise((resolve) => {
          pendingRequests.push(() => {
            original.headers = original.headers || {}
            const csrf = getStoredCsrf()
            if (csrf) {
              applyHeader(original.headers, CSRF_HEADER, csrf)
            }
            resolve(api(original))
          })
        })
      }

      original._retry = true
      try {
        // SECURITY: Refresh token (access token is now in HTTP-only cookie)
        const refreshed = await refreshTokenProactively()
        
        if (refreshed) {
          // Token refreshed successfully, retry the original request
          // Access token is automatically sent via HTTP-only cookie
          original.headers = original.headers || {}
          const csrf = getStoredCsrf()
          if (csrf) {
            applyHeader(original.headers, CSRF_HEADER, csrf)
          }
          return api(original)
        } else {
          // Refresh failed (e.g. missing/expired cookies). Instead of silently redirecting,
          // show a modal so the user understands what's happening and can try to continue.
          // Teachers (OTP) are handled separately below (no modal).
          if (isTeacherSession()) {
            logger.warn('Teacher session refresh failed after 401 — redirecting to OTP login (no modal)')
            isShowingModal = false
            modalResolved = false
            pendingRequests = []
            localStorage.removeItem(CSRF_STORAGE_KEY)
            localStorage.removeItem('isAuthenticated')
            localStorage.removeItem('currentUser')
            window.location.href = otpLoginHref()
            return Promise.reject(error)
          }

          // Mark that we're showing the modal and queue this request
          isShowingModal = true
          modalResolved = false

          const originalRequestForRetry = { ...original }
          const { showTokenExpirationWarning } = useTokenExpiration()

          const handleLogout = () => {
            logger.info('User chose Logout after refresh failure')
            modalResolved = false
            isShowingModal = false
            const requestsToReject = [...pendingRequests]
            pendingRequests = []
            requestsToReject.forEach((cb) => {
              try { cb() } catch {}
            })
            try {
              useAuthStore().clearAuthState()
            } catch {
              localStorage.removeItem(CSRF_STORAGE_KEY)
              localStorage.removeItem('isAuthenticated')
              localStorage.removeItem('currentUser')
            }
            window.location.href = '/login'
          }

          const handleContinue = async () => {
            try {
              logger.debug('User clicked Continue after refresh failure — retrying refresh')
              const ok = await refreshTokenProactively()
              if (!ok) {
                handleLogout()
                return
              }
              modalResolved = true
              isShowingModal = false
              const requestsToRetry = [...pendingRequests]
              pendingRequests = []
              requestsToRetry.forEach((cb) => {
                try { cb() } catch (err) { logger.error('Error retrying queued request:', err) }
              })
            } catch (refreshErr) {
              logger.error('Token refresh error after modal continue:', refreshErr)
              handleLogout()
            }
          }

          const expiresIn = await fetchSessionExpiresIn()
          showTokenExpirationWarning({
            title: 'Session Expired',
            message: 'Your session has expired. Would you like to continue your session or logout?',
            continueText: 'Yes, Continue',
            logoutText: 'No, Logout',
            countdownSeconds: resolveSessionCountdownSeconds(expiresIn),
            onContinue: handleContinue,
            onLogout: handleLogout
          })

          return new Promise((resolve, reject) => {
            pendingRequests.push(() => {
              if (modalResolved) {
                originalRequestForRetry.headers = originalRequestForRetry.headers || {}
                const csrf = getStoredCsrf()
                if (csrf) {
                  applyHeader(originalRequestForRetry.headers, CSRF_HEADER, csrf)
                }
                resolve(api(originalRequestForRetry))
              } else {
                reject(new Error('Session expired'))
              }
            })
          })
        }
      } catch (e) {
        isRefreshing = false

        // Teachers (OTP): no session modal — clear client state and send to OTP login if refresh failed
        if (isTeacherSession()) {
          logger.warn('Teacher session refresh failed after 401 — redirecting to OTP login (no modal)')
          isShowingModal = false
          modalResolved = false
          pendingRequests = []
          localStorage.removeItem(CSRF_STORAGE_KEY)
          localStorage.removeItem('isAuthenticated')
          localStorage.removeItem('currentUser')
          window.location.href = otpLoginHref()
          return Promise.reject(error)
        }

        // Mark that we're showing the modal
        isShowingModal = true
        modalResolved = false
        
        // Store the original request for retry
        const originalRequestForRetry = { ...original }
        
        // Show token expiration warning before logging out
        // Use shared singleton instance to ensure modal shows
        const { showTokenExpirationWarning } = useTokenExpiration()
        
        logger.warn('Token expired - showing warning modal')
        
        const handleLogout = () => {
          logger.info('User clicked Logout or auto-logout triggered')
          
          // Set flags and get pending requests BEFORE clearing
          modalResolved = false
          isShowingModal = false
          const requestsToReject = [...pendingRequests]
          pendingRequests = []
          
          // Reject all pending requests (they will check modalResolved which is now false)
          requestsToReject.forEach((cb) => {
            try {
              cb() // This will reject since modalResolved is false
            } catch (err) {
              // Ignore errors from rejection
            }
          })
          
          try {
            useAuthStore().clearAuthState()
          } catch {
            localStorage.removeItem(CSRF_STORAGE_KEY)
            localStorage.removeItem('isAuthenticated')
            localStorage.removeItem('currentUser')
          }
          window.location.href = '/login'
        }
        
        const handleContinue = async () => {
          try {
            logger.debug('User clicked Continue - attempting token refresh')
            // SECURITY: Refresh token (access token is now in HTTP-only cookie)
            const refreshed = await refreshTokenProactively()
            if (refreshed) {
              logger.info('Token refreshed successfully')
              
              // Set flags and get pending requests BEFORE clearing the array
              modalResolved = true
              isShowingModal = false
              const requestsToRetry = [...pendingRequests]
              pendingRequests = []
              
              // Retry all pending requests (they will check modalResolved which is now true)
              // This includes the original request that triggered the modal
              requestsToRetry.forEach((cb) => {
                try {
                  cb() // This will resolve and retry since modalResolved is true
                } catch (err) {
                  logger.error('Error retrying queued request:', err)
                }
              })
            } else {
              logger.error('Token refresh failed')
              handleLogout()
            }
          } catch (refreshError) {
            logger.error('Token refresh error:', refreshError)
            handleLogout()
          }
        }
        
        const expiresIn = await fetchSessionExpiresIn()
        showTokenExpirationWarning({
          title: 'Session Expired',
          message: 'Your session has expired. Would you like to continue your session or logout?',
          continueText: 'Yes, Continue',
          logoutText: 'No, Logout',
          countdownSeconds: resolveSessionCountdownSeconds(expiresIn),
          onContinue: handleContinue,
          onLogout: handleLogout
        })
        
        // Queue this request to be retried when modal is resolved
        return new Promise((resolve, reject) => {
          pendingRequests.push(() => {
            if (modalResolved) {
              // User chose to continue - retry request
              originalRequestForRetry.headers = originalRequestForRetry.headers || {}
              const csrf = getStoredCsrf()
              if (csrf) {
                applyHeader(originalRequestForRetry.headers, CSRF_HEADER, csrf)
              }
              resolve(api(originalRequestForRetry))
            } else {
              // User chose logout - reject the request
              reject(new Error('Session expired'))
            }
          })
        })
      }
    }
    return Promise.reject(error)
  }
)

export default api

